import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { View, Pressable } from "react-native-web";
import CustomText from "../../components/atoms/CustomText";
import { useResponsive } from "../../hooks/useResponsive";
import { spacing, colors, typography } from "../../utils/styling";
import { resolveCoreSection } from "../../utils/quizinfo/personalityContent";
import { PERSONALITY_COLORS, fromSlug, toSlug } from "../../components/personality/personalityMeta";

// "Financial Resources & Tips" from the personality side panel (McKayla 2026-10-12).
// Tips come from the personality's own content. The two topic lists are hers, verbatim
// from the Resources section of her Website Copy (2026-09-20); the articles behind them
// are not written yet, so they are shown as coming soon rather than as dead links.
// Content is picked by us and approved by her (Tyler 2026-10-12) — nothing here is
// pulled in automatically.
const MONEY_AND_MINDSET = [
  "Your Childhood Money Story",
  "How Your Money Personality Influences Financial Decisions",
  "The Difference Between Financial Goals and Financial Motivation",
  "How Couples with Different Money Personalities Can Talk About Money",
  "Why Financial Stress Looks Different for Everyone",
  "Understanding Your Financial Blind Spots",
];

const MONEY_IN_ACTION = [
  "Goal-Setting Worksheet",
  "Spending & Savings Check-In",
  "Financial Priorities Exercise",
  "Net Worth Tracker",
  "Budgeting Resources",
  "Preparing for a Financial Conversation",
  "Annual Financial Check-In",
];

export default function PersonalityResources() {
  const { personality } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const name = personality ? fromSlug(personality) : null;

  if (!name) return <Navigate to="/personalities" replace />;

  const section = resolveCoreSection(name, "PersonalityResources");
  const color = PERSONALITY_COLORS[name] || colors.primary;

  const card = {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: isMobile ? spacing.lg : spacing.xl,
    marginBottom: spacing.xl,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  };
  const heading = {
    fontSize: isMobile ? 20 : 24,
    fontWeight: "700" as const,
    color: colors.dark,
    fontFamily: typography.fontFamily.body,
    marginBottom: spacing.sm,
  };
  const body = {
    fontSize: isMobile ? 15 : 16,
    color: colors.gray[700],
    fontFamily: typography.fontFamily.body,
    lineHeight: 24,
  };

  const topicList = (title: string, intro: string, topics: string[]) => (
    <View style={card}>
      <CustomText style={heading}>{title}</CustomText>
      <CustomText style={{ ...body, marginBottom: spacing.md }}>{intro}</CustomText>
      <View style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm }}>
        {topics.map((t) => (
          <View
            key={t}
            style={{
              flexBasis: isMobile ? "100%" : 300,
              flexGrow: 1,
              backgroundColor: `${color}12`,
              border: `1px solid ${color}40`,
              borderRadius: 12,
              padding: spacing.md,
            }}
          >
            <CustomText style={{ ...body, fontWeight: "600", color: colors.dark }}>{t}</CustomText>
            <CustomText style={{ fontSize: 12, color: colors.gray[500], fontFamily: "Nunito", marginTop: 2 }}>
              Coming soon
            </CustomText>
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <div className="mp-fade-bg" style={{ width: "100%", caretColor: "transparent", minHeight: "100vh" }}>
      <View
        style={{
          width: "100%",
          alignItems: "center",
          paddingLeft: isMobile ? spacing.lg : spacing.xxl,
          paddingRight: isMobile ? spacing.lg : spacing.xxl,
          marginTop: isMobile ? 100 : 170,
          marginBottom: spacing.xxl,
        }}
      >
        <View style={{ width: "100%", maxWidth: 1000 }}>
          <Pressable
            onPress={() => navigate(`/personalities/${toSlug(name)}`)}
            style={{ marginBottom: spacing.lg, cursor: "pointer" }}
          >
            <CustomText style={{ color: colors.gray[500], fontSize: 14, fontFamily: typography.fontFamily.body }}>
              ← Back to the {name}
            </CustomText>
          </Pressable>

          <View style={{ ...card, borderTop: `4px solid ${color}` }}>
            <CustomText
              style={{
                fontSize: isMobile ? 30 : 40,
                fontWeight: "700",
                color: colors.dark,
                fontFamily: typography.fontFamily.body,
                marginBottom: spacing.sm,
              }}
            >
              Financial Resources & Tips
            </CustomText>
            <CustomText style={{ ...body, fontSize: isMobile ? 16 : 18 }}>
              Understanding your Money Personality is the beginning. Here are ways for the {name} to
              put what you've learned into practice.
            </CustomText>
          </View>

          {section.GrowthOpportunities?.length > 0 && (
            <View style={card}>
              <CustomText style={heading}>Tips for the {name}</CustomText>
              {section.GrowthOpportunities.map((tip, i) => (
                <View
                  key={i}
                  style={{
                    backgroundColor: `${color}12`,
                    borderLeft: `4px solid ${color}`,
                    borderRadius: 8,
                    padding: spacing.md,
                    marginBottom: spacing.sm,
                  }}
                >
                  <CustomText style={body}>{tip}</CustomText>
                </View>
              ))}
              {section.WatchOut ? (
                <CustomText style={{ ...body, marginTop: spacing.sm }}>
                  <b>Watch-out:</b> {section.WatchOut}
                </CustomText>
              ) : null}
            </View>
          )}

          {topicList(
            "Money & Mindset",
            "Understand the why behind your money: how your experiences, beliefs and behaviors shape the way you save, spend, invest and plan.",
            MONEY_AND_MINDSET
          )}
          {topicList(
            "Money in Action",
            "Knowing yourself is powerful. Using that knowledge is where things start to change.",
            MONEY_IN_ACTION
          )}
        </View>
      </View>
    </div>
  );
}
